import { Link } from 'react-router';
import { BOOKING_URL, buttonSm } from '@/lib/bw';

/**
 * 76px sticky nav, per the design system's layout rules -- and the only sticky
 * element on the page.
 *
 * The service pages are deliberately not linked from here -- the footer is
 * their only entry point in the site chrome, which keeps this bar down to the
 * three destinations someone actually navigates to mid-page.
 *
 * The horizontal lockup (mascot + wordmark) is the system's designated nav
 * mark, minimum height 28px. The official assets/logo-horizontal-duo.png could
 * not be fetched -- it exceeds the design tool's 256 KiB per-file read cap --
 * so this composes the two supplied pieces at the documented clear space (the
 * height of the "B"). Replace with the single official file when available.
 *
 * Both source PNGs were 6001x6001 with ~70% transparent padding, which made
 * height-based sizing render them far smaller than requested. They are trimmed
 * to their ink bounds in public/ as wordmark-black.png and mascot-main-duo.png.
 */

/* Rooted at "/" rather than bare fragments: from a service page a bare "#work"
   resolves against that page and goes nowhere. */
const links = [
  { label: 'Our Work', href: '/#work' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'FAQ', href: '/#faq' },
];

/* Eyebrow caps are only 14px tall, well under any touch minimum, and these
   appear from 768px up where the pointer is often a finger. Padding grows the
   target inside the 76px bar; the negative margin keeps the row as designed. */
const navItem =
  'bw-eyebrow -my-4 whitespace-nowrap rounded-[4px] py-4 text-bw-ink transition-colors hover:text-yellow-700 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-bw-ink focus-visible:ring-offset-2';

export function SiteNav() {
  return (
    <header className="sticky top-0 z-50 border-b-2 border-bw-ink bg-white">
      <div className="mx-auto flex h-[76px] w-full max-w-[1200px] items-center justify-between gap-6 px-6">
        <Link
          to="/"
          aria-label="Butter Websites home"
          className="flex shrink-0 items-center gap-2.5 rounded-[8px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-bw-ink focus-visible:ring-offset-2"
        >
          {/* Both marks are above the fold in the sticky bar, so they load
              eagerly at high priority -- the equivalent of next/image's
              `priority`. Intrinsic width/height are still declared so the row
              reserves its space before the PNGs arrive. */}
          <img
            src="/mascot-main-duo.png"
            alt=""
            aria-hidden="true"
            height={539}
            width={600}
            fetchPriority="high"
            decoding="async"
            className="h-[42px] w-auto md:h-[50px]"
          />
          <img
            src="/wordmark-black.png"
            alt="Butter Websites"
            height={357}
            width={900}
            fetchPriority="high"
            decoding="async"
            className="h-[30px] w-auto md:h-[36px]"
          />
        </Link>

        {/* Links and the CTA travel together on the right, so the lockup owns
            the left edge and everything actionable sits in one place. */}
        <div className="flex items-center gap-6 lg:gap-8">
          <nav
            aria-label="Primary"
            className="hidden items-center gap-6 md:flex lg:gap-8"
          >
            {links.map((link) => (
              <Link key={link.href} to={link.href} className={navItem}>
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Plain anchor, not a router Link: cal.com is off-site, and a Link
              would try to resolve it as an in-app route. */}
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            /* min-h rather than more padding: the small button's own height is
               37px, under the 44px touch minimum, and this is the bar's primary
               control. Unconditional -- tablets are touch devices too, and 44px
               sits comfortably inside the 76px bar at every width. */
            className={`${buttonSm} min-h-[44px] shrink-0 bg-bw-yellow text-bw-ink`}
          >
            Book a call
          </a>
        </div>
      </div>
    </header>
  );
}
